import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import styled from 'styled-components';

// Cookie Kategorien
interface CookieCategory {
  name: keyof typeof defaultConsentState;
  checked: boolean;
}

// Standard-Consent-Zustand
const defaultConsentState = {
  necessary: true, // Immer aktiviert und kann nicht deaktiviert werden
  preferences: false,
  statistics: false,
  marketing: false
};

// Styled Components für das Cookie Banner
const CookieBanner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #fff;
  box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  z-index: 1000;
  border-top: 1px solid #eaeaea;
  font-family: 'Arial', sans-serif;
`;

const BannerContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const BannerText = styled.div`
  flex: 1;
`;

const BannerTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  font-weight: 600;
`;

const BannerDescription = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #555;
`;

const BannerButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  @media (min-width: 768px) {
    flex-shrink: 0;
    justify-content: flex-end;
  }
`;

const Button = styled.button<{ primary?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid ${props => props.primary ? '#384860' : '#ccc'};
  background-color: ${props => props.primary ? '#384860' : 'transparent'};
  color: ${props => props.primary ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.primary ? '#93cbb6' : '#f0f0f0'};
    color: ${props => props.primary ? '#384860' : '#333'};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
`;

const Modal = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eaeaea;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: #555;
  
  &:hover {
    color: #000;
  }
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CategoryItem = styled.div`
  display: flex;
  align-items: flex-start;
`;

const CategoryCheckbox = styled.div`
  margin-right: 1rem;
  margin-top: 0.25rem;
`;

const CategoryContent = styled.div`
  flex: 1;
`;

const CategoryTitle = styled.h4`
  margin: 0 0 0.25rem 0;
  font-size: 1.1rem;
  font-weight: 500;
`;

const CategoryDescription = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #666;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 1rem;
  border-top: 1px solid #eaeaea;
`;

const PrivacyLink = styled.a`
  color: #93cbb6;
  text-decoration: none;
  font-size: 0.9rem;
  
  &:hover {
    text-decoration: underline;
    color: #ff6f59;
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

// Haupt-Komponente
const CookieConsent: React.FC = () => {
  const { translations, language } = useLanguage();
  const t = translations.cookieConsent;
  
  // States
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [consentState, setConsentState] = useState({ ...defaultConsentState });
  
  // Beim ersten Laden prüfen, ob bereits Cookie-Einstellungen gespeichert sind
  useEffect(() => {
    const savedConsent = localStorage.getItem('cookieConsent');
    
    if (!savedConsent) {
      setShowBanner(true);
    } else {
      try {
        const savedState = JSON.parse(savedConsent);
        setConsentState(savedState);
        // Gespeicherte Einstellungen sofort anwenden
        setTimeout(() => {
          applyConsentSettings(savedState);
        }, 0);
      } catch (e) {
        setShowBanner(true);
      }
    }
  }, []);
  
  // Cookie-Kategorien basierend auf dem aktuellen Zustand
  const cookieCategories: CookieCategory[] = [
    { name: 'necessary', checked: consentState.necessary },
    { name: 'preferences', checked: consentState.preferences },
    { name: 'statistics', checked: consentState.statistics },
    { name: 'marketing', checked: consentState.marketing }
  ];
  
  // Funktionen zum Verwalten der Zustimmung
  const acceptAllCookies = () => {
    const newState = {
      necessary: true,
      preferences: true,
      statistics: true,
      marketing: true
    };
    
    setConsentState(newState);
    saveConsent(newState);
    setShowBanner(false);
    setShowModal(false);
  };
  
  const acceptNecessaryCookies = () => {
    const newState = { ...defaultConsentState };
    
    setConsentState(newState);
    saveConsent(newState);
    setShowBanner(false);
    setShowModal(false);
  };
  
  const saveConsent = (state: typeof consentState) => {
    localStorage.setItem('cookieConsent', JSON.stringify(state));
    
    // Hier könnten GTM-Events oder andere Cookie-Management-Logik ausgelöst werden
    applyConsentSettings(state);
  };
  
  const handleSaveSettings = () => {
    saveConsent(consentState);
    setShowBanner(false);
    setShowModal(false);
  };
  
  const handleCategoryChange = (category: keyof typeof consentState) => {
    if (category === 'necessary') return; // Notwendige Cookies können nicht deaktiviert werden
    
    setConsentState(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  // Funktion zum Anwenden der Cookie-Einstellungen
  const applyConsentSettings = useCallback((settings: typeof consentState) => {
    // Google Consent Mode Update (V2)
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': settings.marketing ? 'granted' : 'denied',
        'ad_user_data': settings.marketing ? 'granted' : 'denied',
        'ad_personalization': settings.marketing ? 'granted' : 'denied',
        'analytics_storage': settings.statistics ? 'granted' : 'denied',
        'functionality_storage': settings.preferences ? 'granted' : 'denied',
        'personalization_storage': settings.preferences ? 'granted' : 'denied',
        'security_storage': 'granted'
      });
    }

    // Google Analytics/Tag Manager laden
    // Mit Consent Mode können diese Scripte immer geladen werden, 
    // sie respektieren die oben gesetzten Consent-Einstellungen.
    loadGoogleAnalytics();
    loadGoogleTagManager();
    
    // Google Maps (wenn Marketing-Cookies akzeptiert)
    if (settings.marketing) {
      loadGoogleMaps();
    }
    
    // Google Fonts (wenn Präferenzen-Cookies akzeptiert)
    if (settings.preferences) {
      loadGoogleFonts();
    }
    
    // reCAPTCHA (wenn Statistik-Cookies akzeptiert - Security/Spam-Schutz)
    if (settings.statistics) {
      loadGoogleRecaptcha();
    }
  }, []);
  
  // Google Analytics laden (Veraltet: wird jetzt über GTM gesteuert, hier nur für Fallback/Direct Config)
  const loadGoogleAnalytics = () => {
    // Da wir GTM in der index.html haben, müssen wir hier nichts mehr injecten.
    // Wir stellen nur sicher, dass GA4 konfiguriert ist, falls nötig.
    if (window.gtag) {
      window.gtag('config', 'G-V7MYFM12DV', { anonymize_ip: true });
      window.gtag('config', 'AW-836926421', { anonymize_ip: true });
    }
  };
  
  // Google Analytics deaktivieren (Veraltet durch Consent Mode, aber als Fallback behalten)
  const disableGoogleAnalytics = () => {
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      });
    }
    
    // gtag opt-out für beide Analytics-Versionen
    window['ga-disable-G-V7MYFM12DV'] = true;
    window['ga-disable-AW-836926421'] = true;
    
    console.log('Google Analytics (GA4 + Ads) disabled via Consent Mode and opt-out');
  };
  
  // Google Maps laden
  const loadGoogleMaps = () => {
    if (window.google && window.google.maps) return; // Bereits geladen
    
    const script = document.createElement('script');
    script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyDZzTcUSZQnV-H_9R26rrsiuUMdwLp_Jps&libraries=places';
    document.head.appendChild(script);
    
    script.onload = () => {
      console.log('Google Maps loaded');
    };
  };
  
  // Google Fonts laden
  const loadGoogleFonts = () => {
    // Prüfen ob bereits geladen
    const existingLink = document.querySelector('link[href*="fonts.googleapis.com"]');
    if (existingLink) return;
    
    // Preconnect für bessere Performance
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    
    // Font CSS laden
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&family=Montserrat:wght@400;700&family=Open+Sans:wght@400;600;700&family=Playfair+Display:wght@400;700&display=swap';
    document.head.appendChild(link);
    
    console.log('Google Fonts loaded');
  };
  
  // Google Tag Manager laden (Veraltet: wird jetzt über index.html geladen)
  const loadGoogleTagManager = () => {
    // GTM ist bereits in index.html, wir setzen hier nur den Status
    window.gtm_loaded = true;
  };
  
  // Google Tag Manager deaktivieren
  const disableGoogleTagManager = () => {
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied'
      });
    }
    
    // GTM opt-out
    window['ga-disable-GTM-T8B7GK2G'] = true;
    
    window.gtm_loaded = false;
    console.log('Google Tag Manager disabled via Consent Mode');
  };

  // Google reCAPTCHA laden
  const loadGoogleRecaptcha = () => {
    if (window.grecaptcha) return; // Bereits geladen
    
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=6LeSokYrAAAAALyGmEFwUzB29OBC-xTv1Ueayyac';
    document.head.appendChild(script);
    
    script.onload = () => {
      console.log('Google reCAPTCHA loaded');
    };
  };
  
  // URLs für Datenschutz je nach Sprache
  const privacyPolicyUrl = {
    de: '/datenschutz',
    en: '/privacy-policy',
    es: '/politica-de-privacidad',
    ru: '/politika-konfidentsialnosti'
  }[language];
  
  // Funktion zum erneuten Öffnen der Cookie-Einstellungen
  const reopenCookieSettings = () => {
    setShowModal(true);
  };
  
  // Funktion global verfügbar machen für andere Komponenten
  React.useEffect(() => {
    window.reopenCookieSettings = reopenCookieSettings;
    return () => {
      delete window.reopenCookieSettings;
    };
  }, []);
  
  // Wenn kein Banner angezeigt werden soll, nichts rendern
  if (!showBanner && !showModal) return null;
  
  return (
    <>
      {showBanner && (
        <CookieBanner>
          <BannerContent>
            <BannerText>
              <BannerTitle>{t.title}</BannerTitle>
              <BannerDescription>{t.description}</BannerDescription>
            </BannerText>
            <BannerButtons>
              <Button onClick={() => setShowModal(true)}>{t.settings}</Button>
              <Button onClick={acceptNecessaryCookies}>{t.acceptNecessary}</Button>
              <Button primary onClick={acceptAllCookies}>{t.acceptAll}</Button>
            </BannerButtons>
          </BannerContent>
        </CookieBanner>
      )}
      
      {showModal && (
        <ModalOverlay>
          <Modal>
            <ModalHeader>
              <ModalTitle>{t.title}</ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
            </ModalHeader>
            
            <CategoryList>
              {cookieCategories.map((category) => (
                <CategoryItem key={category.name}>
                  <CategoryCheckbox>
                    <Checkbox 
                      type="checkbox"
                      checked={category.checked}
                      onChange={() => handleCategoryChange(category.name)}
                      disabled={category.name === 'necessary'} // Notwendige Cookies können nicht deaktiviert werden
                    />
                  </CategoryCheckbox>
                  <CategoryContent>
                    <CategoryTitle>{t.categories[category.name].title}</CategoryTitle>
                    <CategoryDescription>{t.categories[category.name].description}</CategoryDescription>
                  </CategoryContent>
                </CategoryItem>
              ))}
            </CategoryList>
            
            <ModalFooter>
              <PrivacyLink href={privacyPolicyUrl}>{t.privacyPolicy}</PrivacyLink>
              <Button primary onClick={handleSaveSettings}>{t.save}</Button>
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}
    </>
  );
};

export default CookieConsent; 